import apiService from '../api/apiService';
import { AxiosResponse } from 'axios';
import { PaymentOverviewResponse, PaymentCalculationSummaryResponse } from '../types/paymentTypes';

const PaymentController = {
  /**
   * Get payment overview data for the merchant
   * @param monthYear Optional parameter in YYYY-MM format
   * @returns Payment overview data
   */
  getPaymentOverview: (monthYear?: string): Promise<AxiosResponse<PaymentOverviewResponse>> => {
    let url = '/payments/merchant/overview';
    if (monthYear) {
      // Backend reads the param as `yearMonth` (yyyy-MM); sending `monthYear`
      // left it null, so the month dropdown silently did nothing.
      url += `?yearMonth=${monthYear}`;
    }
    return apiService.get<PaymentOverviewResponse>(url);
  },

  /**
   * Get payment calculation summary for the merchant
   * @param monthYear Optional parameter in YYYY-MM format
   * @returns Payment calculation summary data
   */
  getPaymentCalculationSummary: (monthYear?: string): Promise<AxiosResponse<PaymentCalculationSummaryResponse>> => {
    let url = '/payments/merchant/calculation-summary';
    if (monthYear) {
      url += `?yearMonth=${monthYear}`;
    }
    return apiService.get<PaymentCalculationSummaryResponse>(url);
  },

  /**
   * Get the merchant's own payout list (one row per sub-order payout).
   * Backed by GET /payments/merchant/payouts.
   */
  getMerchantPayouts: (page: number = 0, size: number = 10): Promise<AxiosResponse<any>> =>
    apiService.get<any>('/payments/merchant/payouts', { page, size }),

  /**
   * Get the merchant's monthly settlement statements (one row per settlement run).
   * Each row carries the full payout calculation — gross, commission incl GST,
   * GST, returns, net payout — and, when settled, an invoiceNumber whose PDF
   * receipt can be downloaded via getMerchantSettlementInvoice().
   * Backed by GET /payments/merchant/settlements.
   */
  getMerchantSettlements: (page: number = 0, size: number = 50): Promise<AxiosResponse<any>> =>
    apiService.get<any>('/payments/merchant/settlements', { page, size }),

  /**
   * Download the GST tax-invoice PDF (payout calculation receipt) for one of the
   * merchant's own settlements. Backed by GET /payments/merchant/settlements/{id}/invoice.
   */
  getMerchantSettlementInvoice: (id: string): Promise<AxiosResponse<Blob>> =>
    apiService.getBlob(`/payments/merchant/settlements/${id}/invoice`)
};

export default PaymentController;